import { useState } from 'react';
import { useClientUpdateApi } from '@hero/api-hooks';
import type { Client, Party } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import type { EntityDirectoryPickerSelection } from '../entity-directory/types';
import { hitToParty } from './useClientLinkPickerManager';

/**
 * Manager hook for the per-section "Link" modal on a `Client`. Wraps a
 * small piece of UI state (the picker drawer's open flag + pending
 * pick) plus the save / unlink calls.
 *
 * Accepts selections from the unified `EntityDirectoryPicker` — either
 * an existing directory `hit` (Profile and/or Org) or a freshly
 * suggested `createdProfile`. Both are flattened to a `Party` snapshot
 * matching the `partySchema` invariant (`ids[]` always contains the
 * source `_id` as the first entry; bridged ids when available).
 */
export function useEditClientLinkManager(client: Client | undefined) {
  const updateClient = useClientUpdateApi();
  const [pendingParty, setPendingParty] = useState<Party | undefined>(undefined);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const openPicker = () => setIsPickerOpen(true);
  const closePicker = () => setIsPickerOpen(false);

  const onPick = (selection: EntityDirectoryPickerSelection) => {
    if (selection.hit) {
      setPendingParty(hitToParty(selection.hit));
    } else if (selection.createdProfile) {
      const profileId = selection.createdProfile._id.toString();
      setPendingParty({
        _id: getObjectId(profileId),
        name: selection.createdProfile.name,
        target: 'profile',
        roles: ['business_customer'],
        ids: [{ type: 'profile_id', id: profileId }],
      });
    }
    setIsPickerOpen(false);
  };

  const reset = () => {
    setPendingParty(undefined);
    setIsPickerOpen(false);
    updateClient.reset();
  };

  const onSaveLink = async () => {
    if (!client || !pendingParty) return;
    await updateClient.mutateAsync({
      _id: client._id,
      linkedParty: pendingParty,
    });
  };

  const onUnlink = async () => {
    if (!client) return;
    await updateClient.mutateAsync({
      _id: client._id,
      linkedParty: null,
    });
  };

  return {
    pendingParty,
    setPendingParty,
    isPickerOpen,
    openPicker,
    closePicker,
    onPick,
    reset,
    onSaveLink,
    onUnlink,
    isPending: updateClient.isPending,
    isSuccess: updateClient.isSuccess,
  };
}
